import { ToastProvider } from "@awtoban/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { useEffect } from "react";
import { ensureFreshProvisioning } from "./engine/provision";
import { AuthProvider, useAuth } from "./features/auth/AuthProvider";
import { BindTerminalScreen } from "./features/auth/BindTerminalScreen";
import { LockScreen } from "./features/auth/LockScreen";
import { useIdleLock } from "./lib/useIdleLock";
import { requestPersistentStorage } from "./offline/durability";
import { startSyncManager } from "./offline/syncManager";
import { routeTree } from "./routes/tree";

const queryClient = new QueryClient({
  defaultOptions: {
    // offlineFirst: run the queryFn even when offline (the price preview
    // computes locally via wasm); network queries pause rather than
    // fail-and-retry offline — spec §11.4.
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      networkMode: "offlineFirst",
    },
    mutations: { retry: 0 },
  },
});

const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Auto-lock back to the shift screen after this much inactivity.
const IDLE_LOCK_MS = 2 * 60_000;

function AuthGate() {
  const { phase, endShift } = useAuth();
  useIdleLock(IDLE_LOCK_MS, () => void endShift(), phase === "active");

  // Once a shift is active (so the snapshot endpoints are authorized), make sure
  // the cached cities/distances/tariff aren't stale — re-provision if the
  // server's version tuple changed, and re-check on reconnect.
  useEffect(() => {
    if (phase !== "active") return;
    void ensureFreshProvisioning();
    const onOnline = () => void ensureFreshProvisioning();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [phase]);

  if (phase === "loading") return null;
  if (phase === "unbound") return <BindTerminalScreen />;
  if (phase === "locked") return <LockScreen />;
  return <RouterProvider router={router} />;
}

export function App() {
  // Make IndexedDB durable (un-evictable) and start the background drainer the
  // moment the app loads — so a sale queued in a prior session syncs as soon as
  // we're back online, regardless of auth phase or further operator action.
  useEffect(() => {
    void requestPersistentStorage();
    startSyncManager();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AuthGate />
        <ToastProvider />
      </AuthProvider>
    </QueryClientProvider>
  );
}
